import {
  ArrowsExpandIcon,
  DownloadIcon,
  ExclamationCircleIcon,
  GlobeAltIcon,
  HeartIcon as HeartOutlineIcon,
  PencilIcon,
  RefreshIcon,
  ScissorsIcon,
  ShareIcon,
  SparklesIcon,
  SpeakerphoneIcon,
  ThumbDownIcon,
  ThumbUpIcon,
  VideoCameraIcon,
} from '@heroicons/react/outline';
import { HeartIcon as HeartSolidIcon, TrashIcon } from '@heroicons/react/solid';
import { useMutation, useQuery } from '@tanstack/react-query';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import useLocale from 'next-translate/useTranslation';
import {
  memo,
  type MouseEvent as ReactMouseEvent,
  useEffect,
  useRef,
  useState,
} from 'react';
import { toast } from 'sonner';
import { Image as ImageType } from 'types';

import clsxm from '@/lib/clsxm';
import formatDate from '@/lib/formatDate';

import { SimpleTooltip } from '@/components/ui/tooltip';

import {
  getModelDisplayName,
  modelsWithDescription,
} from '@/constants/imageModels';
import { useAnalytics } from '@/contexts/Analytics';
import { downloadFile } from '@/utils/downloadFile';
import axiosInstance from '@/utils/httpClient';
import { toastError } from '@/utils/toastError';
import { useUser } from '@/utils/useUser';

import EditModal from './EditModal';
import ImageGenerationPreview from './ImageGenerationPreview';
import ShareImageModal from './ShareImageModal';
import SubscriptionPromptOverlay from './SubscriptionPromptOverlay';
import { Upscaler } from './Upscaler';
import { VideoGenerationModal } from './VideoGenerationModal';
import { CopyBtn } from '../atoms/CopyBtn';
import Modal from '../atoms/Modal';
import Toggle from '../atoms/Toggle';
import Button from '../buttons/Button';
import ButtonLink from '../links/ButtonLink';

// Three.js-backed 360° viewer. Lazy-loaded and SSR-disabled so the WebGL
// bundle (~200KB) only ships when a panorama is actually opened — regular
// images never pull it in.
const Panorama360Viewer = dynamic(() => import('./Panorama360Viewer'), {
  ssr: false,
});

/**
 * Helper component rendered when image generation failed on the backend.
 * Mirrors the failed state styling from VideoOutput.
 */
const getFriendlyErrorMessage = (
  errorCode?: string | null,
  t?: (key: string) => string,
) => {
  const translate = t ?? ((key: string) => key);
  if (errorCode === 'moderation_blocked') {
    return translate(
      'Your request was blocked by our content policy. Please adjust your prompt and try again.',
    );
  }
  return translate(
    'We couldn’t generate this image. Please try a different prompt or model.',
  );
};

const FailedGenerationContent = memo(function FailedGenerationContent({
  errorCode,
  onOpenDetails,
  onRegenerate,
  viewMode,
}: {
  errorCode?: string | null;
  onOpenDetails: () => void;
  onRegenerate?: () => void;
  viewMode?: {
    layout: 'grid' | 'row';
    size: 'small' | 'medium' | 'large';
    fill: 'aspect-ratio' | 'square';
  };
}) {
  const { t } = useLocale('ai-images');

  const wrapperClassName =
    viewMode?.fill === 'square'
      ? 'aspect-square w-full'
      : clsxm(
          viewMode?.layout === 'row' ? 'aspect-[4/3]' : 'aspect-square',
          'w-full',
        );

  return (
    <div
      className={clsxm(
        wrapperClassName,
        'flex items-center justify-center rounded-lg bg-gray-50 p-4 dark:bg-gray-900',
      )}
    >
      <div className='text-center'>
        <ExclamationCircleIcon className='mx-auto mb-2 h-8 w-8 text-red-500' />
        <p className='mb-1 text-sm font-medium text-gray-900 dark:text-white'>
          {t('Generation Failed')}
        </p>
        <p className='mb-3 line-clamp-3 text-xs text-gray-500 dark:text-gray-400'>
          {getFriendlyErrorMessage(errorCode, t)}
        </p>
        <div className='flex items-center justify-center gap-3'>
          {onRegenerate && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onRegenerate();
              }}
              className='text-xs text-primary-500 underline hover:text-primary-600'
            >
              {t('Regenerate')}
            </button>
          )}
          <button
            onClick={(e) => {
              e.stopPropagation();
              onOpenDetails();
            }}
            className='text-xs text-primary-500 underline hover:text-primary-600'
          >
            {t('View details')}
          </button>
        </div>
      </div>
    </div>
  );
});

interface ImageOutputProps {
  image: Partial<ImageType>;
  trashView?: boolean;
  hideButtonLabels?: boolean;
  defaultPrompt?: string;
  viewMode?: {
    layout: 'grid' | 'row';
    size: 'small' | 'medium' | 'large';
    fill: 'aspect-ratio' | 'square';
  };
  animateReveal?: boolean;
  generationStartedAt?: number;
  /** If true, shows queue prompt for free users during generation */
  isNotFirstSessionImage?: boolean;
}

const ImageOutput = ({
  image,
  defaultPrompt,
  trashView = false,
  hideButtonLabels = false,
  viewMode,
  animateReveal = false,
  generationStartedAt,
}: ImageOutputProps) => {
  const [isDeleted, setIsDeleted] = useState(false);
  const router = useRouter();
  const { user, isFreePlan, userDetails } = useUser();
  const [localImage, setLocalImage] = useState<Partial<ImageType>>(image);
  const [showModal, setShowModal] = useState(false);
  const [shareWithTeam, setShareWithTeam] = useState(
    Boolean(image.share_with_team),
  );
  const [showUpscaleModal, setShowUpscaleModal] = useState(false);
  const [showShareImageModal, setShowShareImageModal] = useState(false);
  const [upscaledImage, setUpscaledImage] = useState<string>(
    image.upscaled_image_url || '',
  );
  const analytics = useAnalytics();
  const [showEditModal, setShowEditModal] = useState(false);
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [isRemovingBackground, setIsRemovingBackground] = useState(false);
  const [isFavorite, setIsFavorite] = useState(Boolean(image.is_favorite));
  const [showFullPrompt, setShowFullPrompt] = useState(false);

  // A grid <img> that fails to load (a dropped connection on a flaky network,
  // a transient CDN error) never retries on its own: the browser leaves a
  // broken icon with the prompt as alt text, which a customer reported as an
  // "image creation issue" although the file on the CDN was fine
  // (soulwisdommusic@gmail.com, 2026-09-13). Retry a few times with a
  // cache-busting query, then offer a manual reload instead of a dead tile.
  const GRID_IMAGE_MAX_RETRIES = 3;
  const [gridImageRetry, setGridImageRetry] = useState(0);
  const [gridImageFailed, setGridImageFailed] = useState(false);
  const gridImageRetryTimer = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );
  useEffect(() => {
    setGridImageRetry(0);
    setGridImageFailed(false);
  }, [localImage.image_url]);
  useEffect(
    () => () => {
      if (gridImageRetryTimer.current)
        clearTimeout(gridImageRetryTimer.current);
    },
    [],
  );
  const gridImageSrc =
    gridImageRetry > 0 && localImage.image_url
      ? `${localImage.image_url}${
          String(localImage.image_url).includes('?') ? '&' : '?'
        }reload=${gridImageRetry}`
      : `${localImage.image_url}`;
  const handleGridImageError = () => {
    if (!localImage.image_url) return;
    if (gridImageRetry < GRID_IMAGE_MAX_RETRIES) {
      const attempt = gridImageRetry + 1;
      gridImageRetryTimer.current = setTimeout(
        () => setGridImageRetry(attempt),
        1500 * attempt,
      );
      return;
    }
    setGridImageFailed(true);
  };
  const handleGridImageReload = (event: ReactMouseEvent) => {
    event.stopPropagation();
    setGridImageFailed(false);
    setGridImageRetry((count) => count + 1);
  };

  // Track if image is in artificial queue (for sync models with free users)
  const [isInQueue, setIsInQueue] = useState(() => {
    const queuedUntil = (image as any)._queuedUntil;
    return queuedUntil ? Date.now() < queuedUntil : false;
  });

  // Check queue status periodically
  useEffect(() => {
    const queuedUntil = (localImage as any)._queuedUntil;
    if (!queuedUntil) {
      setIsInQueue(false);
      return;
    }

    const checkQueue = () => {
      const stillQueued = Date.now() < queuedUntil;
      setIsInQueue(stillQueued);
      return stillQueued;
    };

    // Initial check
    if (!checkQueue()) return;

    // Check every second
    const interval = setInterval(() => {
      if (!checkQueue()) {
        clearInterval(interval);
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [localImage]);

  const { t } = useLocale('ai-images');

  // if image url is updated, update the local image
  useEffect(() => {
    setLocalImage(image);
    setIsFavorite(Boolean(image.is_favorite));
  }, [image.image_url, image.is_favorite, (image.request as any)?.failed]);

  //useQuery to get the image data
  const query = useQuery(
    ['image', localImage.id],
    () => {
      return axiosInstance.post(`/api/images`, {
        action: 'GET',
        image_id: localImage.id,
      });
    },
    {
      enabled: false,
      onSuccess: (response) => {
        console.log(response.data);
        if (response.data.image.image_url) {
          setLocalImage(response.data.image);
        } else {
          setTimeout(() => {
            query.refetch();
          }, 5000);
        }
      },
    },
  );

  // update the upscaled image
  const updateUpscaledImage = async (image: string) => {
    try {
      const response = await mutation.mutateAsync({
        action: 'UPSCALE',
        upscaled_image_url: image,
      });
      setUpscaledImage(response.data.image.upscaled_image_url);
    } catch (error) {
      console.error(error);
    }
  };

  // getImage until the image is ready
  // const getImage = async () => {
  //   try {
  //     await query.refetch();
  //   } catch (error) {
  //     // toast.error(error as string);
  //   }
  // };

  // useEffect(() => {
  //   if (!localImage.image_url) {
  //     getImage();
  //   }
  // }, [localImage.image_url]);

  const mutation = useMutation(
    (action: string | Record<string, string>) => {
      if (typeof action === 'object') {
        return axiosInstance.post('/api/images', {
          ...action,
          image_id: localImage.id,
          shareWithTeam,
        });
      } else {
        return axiosInstance.post('/api/images', {
          action,
          image_id: localImage.id,
          shareWithTeam,
        });
      }
    },
    {
      onSuccess: (response) => {
        toast.success(response.data.message);
        // A downvote may have refunded credits — let credit counters refresh.
        if (response.data?.refunded && typeof window !== 'undefined') {
          window.dispatchEvent(new Event('credits-refreshed'));
        }
      },
    },
  );

  const shareWithTeamMutation = useMutation(
    (vars: { shareWithTeam: boolean }) => {
      return axiosInstance.post('/api/images', {
        action: 'SHARE_WITH_TEAM',
        image_id: localImage.id,
        shareWithTeam: vars.shareWithTeam,
      });
    },
    {
      onSuccess: (response) => {
        toast.success(response.data.message);
      },
    },
  );

  const handleRemove = async () => {
    if (window.confirm(t('Are you sure you want to delete this image?'))) {
      try {
        await mutation.mutateAsync('REMOVE');
        setIsDeleted(true);
        setShowModal(false);
        analytics.track('Image Deleted', {
          image_id: localImage.id,
        });
      } catch (error) {
        toastError(error);
      }
    }
  };

  const handleRestore = async () => {
    try {
      await mutation.mutateAsync('RESTORE');
      setIsDeleted(true);
      analytics.track('Image Restored', {
        image_id: localImage.id,
      });
    } catch (error) {
      toastError(error);
    }
  };

  const handleVotePlus = async (type: string) => {
    try {
      await mutation.mutateAsync(type);
      analytics.track(type === 'UPVOTE' ? 'Image Upvoted' : 'Image Downvoted', {
        image_id: localImage.id,
        url: localImage.image_url as string,
      });
    } catch (error) {
      toastError(error);
    }
  };

  const handle4kDownload = async () => {
    if (isFreePlan) {
      analytics.track('Image 4K Download Attempted', {
        image_id: localImage.id,
      });
      toast.warning(() => (
        <div>
          <div className='mb-5'>
            {t(
              `Please note, downloading images in 4K quality is a premium feature available only for paid plans. Consider upgrading your plan to access this and other advanced features. Thank you for understanding!`,
            )}
          </div>
          <ButtonLink variant='dark' href='/settings/billing'>
            {t('Upgrade plan')}
          </ButtonLink>
        </div>
      ));
      return;
    }

    if (localImage.four_k_image_url) {
      window.open(localImage.four_k_image_url, '_blank');
    } else {
      // call the api to generate the 4k image
      try {
        const response = await mutation.mutateAsync('4K');
        setLocalImage(response.data.image);

        analytics.track('Image 4K Generated', {
          image_id: localImage.id,
        });

        window.open(response.data.image.four_k_image_url, '_blank');
      } catch (error) {
        toastError(error);
      }
    }
  };

  const handleShareWithTeam = async () => {
    const nextValue = !shareWithTeam;
    setShareWithTeam(nextValue);
    try {
      await shareWithTeamMutation.mutateAsync({ shareWithTeam: nextValue });
      toast.success('Image settings saved successfully 👍');
    } catch (error) {
      console.error(error);
      toast.error('Failed to save image settings 😞');
    }
  };

  const handleRegenerate = () => {
    // The generation params live in `request` — its shape varies per model
    // branch in /api/generate-image, so read every field defensively.
    const req = (localImage.request as Record<string, any>) || {};

    analytics.track('Image Regenerate', { image_id: localImage.id });

    // Close the detail modal — when already on the target page the
    // navigation below won't unmount this component, so the modal must
    // be closed here.
    setShowModal(false);

    // Edit flows store their source images under a per-branch key
    // (originalImages/reference_images/imageUrls/image_urls/images arrays,
    // or a single image/image_url) — restore them so the regeneration is
    // an edit again.
    const sourceArray = [
      req.originalImages,
      req.reference_images,
      req.imageUrls,
      req.image_urls,
      req.images,
    ].find((value) => Array.isArray(value) && value.length > 0);
    const sourceSingle = req.image || req.image_url;
    const sourceImages: string[] = (
      sourceArray || (sourceSingle ? [sourceSingle] : [])
    ).filter((url: any) => typeof url === 'string' && url);

    const payload = {
      // user_prompt is the raw prompt as typed; `prompt` has the style baked in.
      prompt: localImage.user_prompt || localImage.prompt || '',
      model: localImage.model || '',
      dimensions: req.dimensions || '',
      resolution: req.resolution || '',
      images: sourceImages,
    };

    try {
      sessionStorage.setItem('regenerate-image', JSON.stringify(payload));
    } catch (error) {
      console.error('Unable to store regenerate params:', error);
    }

    router.push(`/ai-images?regenerate=${Date.now()}`);
  };

  const handleImageUpdate = (updatedImage: Partial<ImageType>) => {
    setLocalImage((prev) => ({ ...prev, ...updatedImage }));
  };

  const handleToggleFavorite = async () => {
    // Optimistic update
    const newFavoriteState = !isFavorite;
    const previousFavoriteState = isFavorite;
    const previousImage = { ...localImage };

    setIsFavorite(newFavoriteState);
    setLocalImage({ ...localImage, is_favorite: newFavoriteState });

    try {
      const response = await axiosInstance.post('/api/images', {
        action: 'TOGGLE_FAVORITE',
        image_id: localImage.id,
      });

      // Update with server response to ensure consistency
      setIsFavorite(response.data.image.is_favorite);
      setLocalImage({
        ...localImage,
        is_favorite: response.data.image.is_favorite,
      });

      analytics.track(
        response.data.image.is_favorite
          ? 'Image Favorited'
          : 'Image Unfavorited',
        {
          image_id: localImage.id,
        },
      );
    } catch (error) {
      // Revert on error
      setIsFavorite(previousFavoriteState);
      setLocalImage(previousImage);
      toastError(error);
    }
  };

  const isPanorama = Boolean((localImage.request as any)?.isPanorama);

  // Provider icon shown next to the model name in the details modal —
  // same lookup the model selector uses.
  const modelDefinition = localImage.model
    ? modelsWithDescription.find(
        (m) => m.title === localImage.model || m.id === localImage.model,
      )
    : undefined;
  const ModelIcon = modelDefinition?.icon;

  const cleanedPrompt = (localImage.prompt || '')
    .replace('4k', '')
    .replace(
      '8k, Highly detailed, smooth, sharp, elegant, the most beautiful image ever seen',
      '',
    )
    .replace('mdjrny-v4 ', '')
    .trim()
    .replace(/(,$)/g, '');
  // Long prompts (image-edit chains, upsampled prompts) would otherwise
  // stretch the details column — clamp them behind a Show more toggle.
  const isLongPrompt = cleanedPrompt.length > 220;

  // Fullscreen: works for both the flat <img> and the WebGL canvas.
  // Uses the browser's native Fullscreen API, so ESC to exit is free.
  const mediaContainerRef = useRef<HTMLDivElement | null>(null);
  const toggleFullscreen = () => {
    const el = mediaContainerRef.current;
    if (!el) return;
    if (document.fullscreenElement) {
      document.exitFullscreen?.();
    } else {
      el.requestFullscreen?.();
    }
  };

  if (isDeleted) return null;

  return (
    <li key={localImage.id} className='relative'>
      <EditModal
        image={localImage}
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
      />
      <Modal
        onClose={() => setShowShareImageModal(false)}
        showModal={showShareImageModal}
        className='overflow-auto sm:max-w-2xl'
      >
        <ShareImageModal image={localImage} />
      </Modal>
      <Modal
        onClose={() => setShowUpscaleModal(false)}
        showModal={showUpscaleModal}
        className='mt-5 overflow-y-auto sm:max-w-[95%]'
        doNotCloseOnOverlayClick={true}
      >
        <Upscaler
          beforeImg={localImage.image_url as string}
          afterImg={upscaledImage}
          onUpscale={(response) => {
            setUpscaledImage(response);
            updateUpscaledImage(response);
          }}
        />
      </Modal>
      <Modal
        onClose={() => setShowModal(false)}
        showModal={showModal}
        className='mt-5 overflow-y-auto sm:max-w-[80%]'
      >
        {/** Show image prompt details */}
        <main className='mx-auto '>
          <div className='grid grid-cols-1 gap-x-8 gap-y-10 px-4 py-16 sm:px-6 md:grid-cols-3 lg:px-8'>
            <div className='flex flex-col text-gray-500 order-2 md:order-1'>
              {(localImage.request as any)?.failed && (
                <div className='mb-4 rounded-lg border border-red-200 bg-red-50 p-4 dark:border-red-900/50 dark:bg-red-900/20'>
                  <div className='flex'>
                    <ExclamationCircleIcon className='h-5 w-5 flex-shrink-0 text-red-500' />
                    <div className='ml-3'>
                      <p className='text-sm font-medium text-red-800 dark:text-red-400'>
                        {t('Generation Failed')}
                      </p>
                      <p className='mt-1 text-xs text-red-700 dark:text-red-300'>
                        {getFriendlyErrorMessage(
                          (localImage.request as any)?.errorCode,
                          t,
                        )}
                      </p>
                    </div>
                  </div>
                </div>
              )}
              <p className='mb-3 flex items-center'>
                <strong>{t('Model')}:</strong>{' '}
                <span className='ml-1 inline-flex items-center gap-1.5 pr-3'>
                  {ModelIcon && <ModelIcon className='h-4 w-4 flex-shrink-0' />}
                  <span translate='no'>
                    {getModelDisplayName(localImage.model || '')}
                  </span>
                </span>
              </p>
              {localImage.prompt && (
                <div className='mb-3'>
                  <div className='flex items-center gap-1'>
                    <strong>{t('Prompt')}:</strong>
                    <CopyBtn
                      value={localImage.prompt as string}
                      showText={false}
                      variant='simple'
                    />
                  </div>
                  <p
                    className={clsxm(
                      'mt-1 break-words',
                      isLongPrompt && !showFullPrompt && 'line-clamp-4',
                    )}
                  >
                    {cleanedPrompt}
                  </p>
                  {isLongPrompt && (
                    <button
                      type='button'
                      onClick={() => setShowFullPrompt((value) => !value)}
                      className='mt-1 text-xs font-medium text-primary-500 hover:text-primary-600'
                    >
                      {showFullPrompt ? t('Show less') : t('Show more')}
                    </button>
                  )}
                </div>
              )}
              {/* Zero-credit rows (rolled-back reservations) carry no charge —
                  hide the row rather than showing a misleading 0. */}
              {typeof localImage.used_credits === 'number' &&
              localImage.used_credits > 0 ? (
                <p className='mb-3 '>
                  <strong>{t('Credits used')}:</strong>{' '}
                  {localImage.used_credits}
                </p>
              ) : null}
              {!localImage.isFromExample && (
                <p>
                  <strong>{t('Created At')}:</strong>{' '}
                  {formatDate(localImage.created_at as string)}
                </p>
              )}

              {image.user_id === user?.id && (
                <div className='mt-5 flex'>
                  <p>
                    <strong>{t('Share with Team')}</strong>
                  </p>

                  <div className='ml-2'>
                    <Toggle
                      initialValue={shareWithTeam}
                      onChange={handleShareWithTeam}
                    />
                  </div>
                </div>
              )}

              {!localImage.isFromExample && (
                <Button
                  variant='outline'
                  className='mt-10 flex items-center justify-center'
                  onClick={handleRegenerate}
                >
                  <RefreshIcon className='mr-2 h-6 w-6' />
                  {t('Regenerate')}
                </Button>
              )}

              {/* edit image */}
              <Button
                variant='outline'
                className={clsxm(
                  localImage.isFromExample ? 'mt-10' : 'mt-5',
                  'flex items-center justify-center',
                )}
                onClick={() => setShowEditModal(true)}
              >
                <PencilIcon className='mr-2 h-6 w-6' />
                {t('Edit image')}
              </Button>

              <Button
                variant='outline'
                className='mt-5 flex items-center justify-center'
                onClick={() => {
                  setShowUpscaleModal(true);
                  setShowModal(false);
                }}
              >
                <SparklesIcon className='mr-2 h-6 w-6' />
                {t('Upscale image')}
              </Button>

              <Button
                variant='outline'
                className='mt-5 flex items-center justify-center'
                onClick={() => {
                  setShowVideoModal(true);
                  setShowModal(false);
                }}
              >
                <VideoCameraIcon className='mr-2 h-6 w-6' />
                {t('Generate video')}
              </Button>

              <Button
                variant='outline'
                className='mt-5 flex items-center justify-center'
                onClick={() => {
                  const imageUrl = encodeURIComponent(
                    localImage.image_url as string,
                  );
                  window.open(`/ai-talking-videos?image=${imageUrl}`, '_blank');
                }}
              >
                <SpeakerphoneIcon className='mr-2 h-6 w-6' />
                {t('Generate Talking Video')}
              </Button>

              <Button
                variant='outline'
                className='mt-5 flex items-center justify-center'
                onClick={() => {
                  if (isFreePlan && userDetails?.bonus_images === 0) {
                    alert(
                      'Please note this feature is only available for paid plans. Consider upgrading your plan to access this and other advanced features. Thank you for understanding!',
                    );
                    return;
                  }
                  const url =
                    localImage.four_k_image_url ||
                    (localImage.image_url as string);
                  const extension =
                    url.split('.').pop()?.toLowerCase() || 'jpg';
                  downloadFile(url, `image.${extension}`);
                  analytics.track('Image Downloaded', {
                    image_id: localImage.id,
                    url,
                  });
                }}
              >
                <DownloadIcon className='mr-2 h-6 w-6' />
                {t('Download  image')}
              </Button>

              {localImage.upscaled_image_url && (
                <Button
                  variant='outline'
                  className='mt-5 flex items-center justify-center'
                  onClick={() => {
                    const url =
                      localImage.upscaled_image_url ||
                      (localImage.image_url as string);
                    const extension =
                      url.split('.').pop()?.toLowerCase() || 'png';
                    downloadFile(url, `upscaled_image.${extension}`);
                    analytics.track('Image Downloaded Upscaled', {
                      image_id: localImage.id,
                      url,
                    });
                  }}
                >
                  <DownloadIcon className='mr-2 h-6 w-6' />
                  {t('Download upscaled image')}
                </Button>
              )}

              <Button
                variant='outline'
                className='mt-5 flex items-center justify-center'
                isLoading={isRemovingBackground}
                onClick={async () => {
                  try {
                    setIsRemovingBackground(true);
                    // Call the API to remove background
                    const response = await axiosInstance.post(
                      '/api/generate-image',
                      {
                        image: localImage.image_url,
                        action: 'Remove Background',
                        model: 'Img2Img',
                      },
                    );

                    if (response.data && response.data[0]) {
                      // Update the local image with the new background-removed image
                      // setLocalImage({
                      //   ...localImage,
                      //   image_url: response.data[0].image_url,
                      // });

                      // Close the current modal
                      setShowModal(false);

                      // Track analytics
                      analytics.track('Background Removed', {
                        image_id: localImage.id,
                      });

                      toast.success(t('Background is being removed...'));
                    }
                  } catch (error) {
                    console.error('Error removing background:', error);
                    toast.error(t('Failed to remove background'));
                  } finally {
                    setIsRemovingBackground(false);
                  }
                }}
              >
                <ScissorsIcon className='mr-2 h-6 w-6' />
                {isRemovingBackground
                  ? t('Removing background...')
                  : t('Remove Background')}
              </Button>

              {!localImage.isFromExample && (
                <button
                  type='button'
                  className='mt-10 flex space-x-2 text-gray-400 hover:text-gray-500'
                  onClick={handleRemove}
                >
                  <TrashIcon className='h-5 w-5' aria-hidden='true' />
                  {t('Delete')}
                </button>
              )}
            </div>
            <div className='relative md:col-span-2 order-1 md:order-2'>
              {(localImage.request as any)?.failed ? (
                <div className='flex h-full w-full items-center justify-center rounded-lg bg-gray-100 p-8 dark:bg-gray-800'>
                  <div className='max-w-md text-center'>
                    <ExclamationCircleIcon className='mx-auto mb-4 h-16 w-16 text-red-500' />
                    <h3 className='mb-2 text-xl font-medium text-gray-900 dark:text-white'>
                      {t('Generation Failed')}
                    </h3>
                    <p className='mb-6 text-sm text-gray-600 dark:text-gray-300'>
                      {getFriendlyErrorMessage(
                        (localImage.request as any)?.errorCode,
                        t,
                      )}
                    </p>
                    <div className='rounded-md bg-white p-4 shadow-sm dark:bg-gray-700'>
                      <p className='text-sm text-gray-500 dark:text-gray-300'>
                        {t(
                          'Try rephrasing the prompt or try a different model:',
                        )}
                      </p>
                      <p className='mt-2 font-medium text-primary-500'>
                        Nano Banana 2, Seedream 4.5, FLUX.2 [pro], Z-Image Turbo
                      </p>
                    </div>
                  </div>
                </div>
              ) : (
                <div
                  ref={mediaContainerRef}
                  className='panorama-fullscreen-fit relative bg-black'
                >
                  {isPanorama && localImage.image_url ? (
                    <>
                      <Panorama360Viewer src={localImage.image_url as string} />
                      <div className='pointer-events-none absolute bottom-2 left-1/2 -translate-x-1/2 rounded-full bg-black/60 px-3 py-1 text-[11px] font-medium text-white'>
                        {t('Drag to look around, scroll to zoom')}
                      </div>
                    </>
                  ) : (
                    <img
                      src={localImage.image_url}
                      alt={localImage.user_prompt as string}
                      title={localImage.user_prompt}
                      loading='lazy'
                      className='object-contain max-h-[90vh] w-full group-hover:opacity-85'
                      onClick={(e) => {
                        if (isFreePlan && userDetails?.bonus_images === 0) {
                          return;
                        }
                        analytics.track('Image Downloaded', {
                          image_id: localImage.id,
                          url:
                            localImage.four_k_image_url ||
                            (localImage.image_url as string),
                        });
                        window.open(
                          localImage.four_k_image_url ||
                            (localImage.image_url as string),
                          '_blank',
                        );
                      }}
                      onContextMenu={(e) => {
                        if (isFreePlan && userDetails?.bonus_images === 0) {
                          e.preventDefault();
                          alert(
                            'Please note this feature is only available for paid plans. Consider upgrading your plan to access this and other advanced features. Thank you for understanding!',
                          );
                          return false;
                        }
                      }}
                    />
                  )}
                  <SimpleTooltip title={t('Fullscreen')}>
                    <button
                      type='button'
                      onClick={toggleFullscreen}
                      className='absolute bottom-2 right-2 z-[60] rounded bg-black/60 p-2 text-white transition-colors hover:bg-black/80'
                      aria-label={t('Fullscreen')}
                    >
                      <ArrowsExpandIcon
                        className='h-4 w-4 sm:h-5 sm:w-5'
                        aria-hidden='true'
                      />
                    </button>
                  </SimpleTooltip>
                </div>
              )}
              {!(localImage.request as any)?.failed && (
                <div className='absolute left-0 top-0 z-50 flex items-center justify-between w-full p-2'>
                  <div className='flex items-center space-x-2 bg-black bg-opacity-50 p-2 rounded'>
                    {localImage.user_id === user?.id && (
                      <SimpleTooltip title={t('Share')}>
                        <button
                          className='text-white hover:text-gray-200'
                          onClick={() => setShowShareImageModal(true)}
                        >
                          <ShareIcon
                            className='h-4 w-4 sm:h-6 sm:w-6'
                            aria-hidden='true'
                          />
                        </button>
                      </SimpleTooltip>
                    )}
                    <SimpleTooltip title={t('Good image')}>
                      <button
                        className='text-white hover:text-gray-200'
                        onClick={() => handleVotePlus('VOTE_PLUS')}
                      >
                        <ThumbUpIcon
                          className='h-4 w-4 sm:h-6 sm:w-6'
                          aria-hidden='true'
                        />
                      </button>
                    </SimpleTooltip>
                    <SimpleTooltip title={t('Bad image')}>
                      <button
                        className='text-white hover:text-gray-200'
                        onClick={() => handleVotePlus('VOTE_MINUS')}
                      >
                        <ThumbDownIcon
                          className='h-4 w-4 sm:h-6 sm:w-6'
                          aria-hidden='true'
                        />
                      </button>
                    </SimpleTooltip>
                  </div>
                  <SimpleTooltip
                    title={
                      isFavorite
                        ? t('Remove from favorites')
                        : t('Add to favorites')
                    }
                  >
                    <button
                      className='text-white hover:text-gray-200 bg-black bg-opacity-50 p-2 rounded'
                      onClick={handleToggleFavorite}
                    >
                      {isFavorite ? (
                        <HeartSolidIcon
                          className='h-4 w-4 sm:h-6 sm:w-6 text-red-500'
                          aria-hidden='true'
                        />
                      ) : (
                        <HeartOutlineIcon
                          className='h-4 w-4 sm:h-6 sm:w-6'
                          aria-hidden='true'
                        />
                      )}
                    </button>
                  </SimpleTooltip>
                </div>
              )}
            </div>
          </div>
        </main>
      </Modal>
      <div className='aspect-w-10 aspect-h-7 group block w-full overflow-hidden rounded-lg bg-gray-100 focus-within:ring-2 focus-within:ring-indigo-500 focus-within:ring-offset-2 focus-within:ring-offset-gray-100'>
        {/* Show queue overlay for free users on 2nd+ generation (even for sync models) */}
        {isFreePlan && isInQueue && (localImage as any)._queuedUntil ? (
          <SubscriptionPromptOverlay
            queuedUntil={(localImage as any)._queuedUntil}
            className={
              viewMode?.fill === 'square'
                ? 'aspect-square w-full'
                : clsxm(
                    viewMode?.layout === 'row'
                      ? 'aspect-[4/3]'
                      : 'aspect-square',
                    'w-full',
                  )
            }
          />
        ) : !localImage.image_url && (localImage.request as any)?.failed ? (
          <FailedGenerationContent
            errorCode={(localImage.request as any)?.errorCode}
            onOpenDetails={() => setShowModal(true)}
            onRegenerate={handleRegenerate}
            viewMode={viewMode}
          />
        ) : (
          <ImageGenerationPreview
            src={localImage.image_url ? gridImageSrc : undefined}
            alt={localImage.user_prompt as string}
            title={localImage.user_prompt}
            animateReveal={animateReveal}
            generationStartedAt={
              generationStartedAt ??
              (localImage.created_at
                ? Date.parse(localImage.created_at)
                : undefined)
            }
            loadFailed={gridImageFailed}
            naturalAspect={
              viewMode?.layout === 'row' && viewMode?.fill !== 'square'
            }
            statusLabel={t('Generating...')}
            imageClassName={
              viewMode?.fill === 'square'
                ? 'h-full w-full cursor-pointer object-cover group-hover:opacity-85'
                : 'max-h-full max-w-full cursor-pointer object-contain group-hover:opacity-85'
            }
            onClick={() => setShowModal(true)}
            onError={handleGridImageError}
          >
            {localImage.image_url && (
              <>
                {gridImageFailed && (
                  <button
                    type='button'
                    onClick={handleGridImageReload}
                    className='absolute inset-0 z-40 flex flex-col items-center justify-center gap-2 bg-gray-100/95 px-4 text-center text-sm text-gray-700 dark:bg-gray-800/95 dark:text-gray-200'
                  >
                    <RefreshIcon className='h-6 w-6' aria-hidden='true' />
                    <span>{t('Image failed to load. Click to retry.')}</span>
                  </button>
                )}
                {isPanorama && (
                  <button
                    type='button'
                    onClick={() => setShowModal(true)}
                    className='absolute left-2 top-2 z-[60] inline-flex items-center gap-1 rounded-full bg-black/60 px-2 py-1 text-[10px] font-semibold uppercase tracking-wide text-white backdrop-blur-sm hover:bg-black/80'
                    aria-label={t('View 360°')}
                  >
                    <GlobeAltIcon className='h-3 w-3' aria-hidden='true' />
                    360°
                  </button>
                )}
                <div
                  className={clsxm(
                    'absolute top-0 right-0 z-50 flex items-center p-2 transition-opacity',
                    isFavorite
                      ? 'opacity-100'
                      : 'opacity-100 sm:opacity-0 sm:group-hover:opacity-100',
                  )}
                >
                  <SimpleTooltip
                    title={
                      isFavorite
                        ? t('Remove from favorites')
                        : t('Add to favorites')
                    }
                  >
                    <button
                      className='text-white hover:text-gray-200 bg-black bg-opacity-50 p-1 rounded'
                      onClick={handleToggleFavorite}
                    >
                      {isFavorite ? (
                        <HeartSolidIcon
                          className='h-4 w-4 sm:h-5 sm:w-5 text-red-500'
                          aria-hidden='true'
                        />
                      ) : (
                        <HeartOutlineIcon
                          className='h-4 w-4 sm:h-5 sm:w-5'
                          aria-hidden='true'
                        />
                      )}
                    </button>
                  </SimpleTooltip>
                </div>
                {localImage.user_id &&
                  localImage.user_id === user?.id &&
                  localImage.share_with_team && (
                    <span className='absolute left-0 top-10 ml-2 inline-flex bg-opacity-50 items-center rounded-md bg-green-50 px-2 py-1 text-xs font-medium text-green-700 ring-1 ring-inset ring-green-600/20'>
                      Shared
                    </span>
                  )}
                {localImage.user_id && localImage.user_id !== user?.id && (
                  <span className='absolute left-0 top-10 ml-2 inline-flex bg-opacity-50 items-center rounded-md bg-primary-50 px-2 py-1 text-xs font-medium text-primary-700 ring-1 ring-inset ring-primary-600/20'>
                    {localImage.profiles?.first_name ||
                    localImage.profiles?.last_name
                      ? `${localImage.profiles.first_name || ''} ${localImage.profiles.last_name || ''}`.trim()
                      : localImage.profiles?.username || 'Team'}
                  </span>
                )}
                <div className='absolute bottom-0 left-0 right-0  flex items-center justify-between bg-black bg-opacity-50 p-2 text-white opacity-100 transition-opacity duration-300 group-hover:opacity-100 sm:opacity-0 sm:group-hover:opacity-100'>
                  <div className='flex items-center space-x-2'>
                    {localImage.user_id === user?.id && (
                      <SimpleTooltip title={t('Share')}>
                        <button
                          className='text-white hover:text-gray-200'
                          onClick={() => setShowShareImageModal(true)}
                        >
                          <ShareIcon
                            className='h-4 w-4 sm:h-6 sm:w-6'
                            aria-hidden='true'
                          />
                        </button>
                      </SimpleTooltip>
                    )}
                    {/* <SimpleTooltip title={t('Good image')}>
                  <button
                    className='text-white hover:text-gray-200'
                    onClick={() => handleVotePlus('VOTE_PLUS')}
                  >
                    <ThumbUpIcon
                      className='h-4 w-4 sm:h-6 sm:w-6'
                      aria-hidden='true'
                    />
                  </button>
                </SimpleTooltip>
                <SimpleTooltip title={t('Bad image')}>
                  <button
                    className='text-white hover:text-gray-200'
                    onClick={() => handleVotePlus('VOTE_MINUS')}
                  >
                    <ThumbDownIcon
                      className='h-4 w-4 sm:h-6 sm:w-6'
                      aria-hidden='true'
                    />
                  </button>
                </SimpleTooltip> */}
                  </div>
                  <div className='flex items-center space-x-2'>
                    <SimpleTooltip title={t('Open')}>
                      <button
                        className='text-white hover:text-gray-200'
                        onClick={() => setShowModal(true)}
                      >
                        <ArrowsExpandIcon
                          className='h-4 w-4 sm:h-6 sm:w-6'
                          aria-hidden='true'
                        />
                      </button>
                    </SimpleTooltip>
                    {!localImage.isFromExample && (
                      <SimpleTooltip title={t('Regenerate')}>
                        <button
                          className='text-white hover:text-gray-200'
                          onClick={handleRegenerate}
                        >
                          <RefreshIcon
                            className='h-4 w-4 sm:h-6 sm:w-6'
                            aria-hidden='true'
                          />
                        </button>
                      </SimpleTooltip>
                    )}
                    <SimpleTooltip title={t('Edit Image')}>
                      <button
                        className='text-white hover:text-gray-200'
                        onClick={() => setShowEditModal(true)}
                      >
                        <PencilIcon
                          className='h-4 w-4 sm:h-6 sm:w-6'
                          aria-hidden='true'
                        />
                      </button>
                    </SimpleTooltip>
                    <SimpleTooltip title={t('Generate Video')}>
                      <button
                        className='text-white hover:text-gray-200'
                        onClick={() => setShowVideoModal(true)}
                      >
                        <VideoCameraIcon
                          className='h-4 w-4 sm:h-6 sm:w-6'
                          aria-hidden='true'
                        />
                      </button>
                    </SimpleTooltip>
                    <SimpleTooltip title={t('Talking Video')}>
                      <button
                        className='text-white hover:text-gray-200'
                        onClick={() => {
                          const imageUrl = encodeURIComponent(
                            localImage.image_url as string,
                          );
                          window.open(
                            `/ai-talking-videos?image=${imageUrl}`,
                            '_blank',
                          );
                        }}
                      >
                        <SpeakerphoneIcon
                          className='h-4 w-4 sm:h-6 sm:w-6'
                          aria-hidden='true'
                        />
                      </button>
                    </SimpleTooltip>
                    <SimpleTooltip title={t('Upscale')}>
                      <button
                        className='text-white hover:text-gray-200'
                        onClick={() => setShowUpscaleModal(true)}
                      >
                        <SparklesIcon
                          className='h-4 w-4 sm:h-6 sm:w-6'
                          aria-hidden='true'
                        />
                      </button>
                    </SimpleTooltip>
                    <SimpleTooltip title={t('Download')}>
                      <button
                        className='text-white hover:text-gray-200'
                        onClick={() => {
                          if (isFreePlan && userDetails?.bonus_images === 0) {
                            toast.warning(
                              <div>
                                <div className='mb-5'>
                                  {t(
                                    'Please note this feature is only available for paid plans. Consider upgrading your plan to access this and other advanced features. Thank you for understanding!',
                                  )}
                                </div>
                                <ButtonLink
                                  variant='dark'
                                  href='/settings/billing'
                                >
                                  {t('Upgrade plan')}
                                </ButtonLink>
                              </div>,
                            );
                            return;
                          }
                          const url =
                            localImage.four_k_image_url ||
                            (localImage.image_url as string);
                          const extension =
                            url.split('.').pop()?.toLowerCase() || 'jpg';
                          downloadFile(url, `image.${extension}`);
                          analytics.track('Image Downloaded', {
                            image_id: localImage.id,
                            url,
                          });
                        }}
                      >
                        <DownloadIcon
                          className='h-4 w-4 sm:h-6 sm:w-6'
                          aria-hidden='true'
                        />
                      </button>
                    </SimpleTooltip>
                  </div>
                </div>
              </>
            )}
          </ImageGenerationPreview>
        )}
      </div>
      <VideoGenerationModal
        isOpen={showVideoModal}
        onClose={() => setShowVideoModal(false)}
        imageUrl={localImage.image_url as string}
        defaultPrompt=''
        isFreePlan={isFreePlan}
      />
    </li>
  );
};

export default memo(ImageOutput);
